import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  CalendarTodayOutlined,
  ConfirmationNumberOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import moment from "moment";

const ESTADO_STYLE = {
  pendiente: { label: "Pendiente de aprobación", bg: "#FFEFC2", color: "#7A4E00" },
  aprobada: { label: "Aprobada", bg: "#B6FECB", color: "#216735" },
  rechazada: { label: "Rechazada", bg: "#FFD6D6", color: "#8A1F1F" },
};

const headCellSx = {
  fontSize: "16px",
  fontWeight: "600",
  color: "#0B0060",
  fontFamily: "Bricolage Grotesque",
};

const MisSolicitudes = ({ solicitudes = [], loading = false, isMobile = false, history }) => {
  const [expandedId, setExpandedId] = useState(null);

  const totalEntradas = (s) =>
    ((s.carrito && s.carrito.items) || []).reduce((acc, it) => acc + (it.cantidad || 0), 0);

  const verTickets = (s) => {
    if (s.idcompra) history.push(`/compra/${s.idcompra}`);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-4">
        <CircularProgress />
      </div>
    );
  }

  if (!solicitudes.length) {
    return (
      <div className="d-flex flex-column align-items-center my-5">
        <small className="text-secondary">No tienes solicitudes.</small>
      </div>
    );
  }

  // MOBILE: cards expandibles (mismo patrón que Mis compras)
  if (isMobile) {
    return (
      <div className="mt-3">
        {solicitudes.map((s) => {
          const estilo = ESTADO_STYLE[s.estado] || ESTADO_STYLE.pendiente;
          const items = (s.carrito && s.carrito.items) || [];
          const abierto = expandedId === s.id;
          return (
            <div key={s.id} className="card mb-3" style={{ borderRadius: "8px", border: "1px solid #E0E0E0" }}>
              <div className="card-body p-3" onClick={() => setExpandedId(abierto ? null : s.id)} style={{ cursor: "pointer" }}>
                <div className="d-flex align-items-center" style={{ width: "100%" }}>
                  <div style={{ marginRight: "10px", flexShrink: 0 }}>
                    {abierto ? <KeyboardArrowUp sx={{ color: "#5656ff" }} /> : <KeyboardArrowDown sx={{ color: "#5656ff" }} />}
                  </div>
                  <div style={{ flex: 1, color: "#0b0060", fontFamily: "Bricolage Grotesque", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.evento?.evento || "Evento"}
                  </div>
                  <Chip size="small" label={estilo.label} sx={{ backgroundColor: estilo.bg, color: estilo.color, fontSize: "11px", height: "22px", fontFamily: "Bricolage Grotesque" }} />
                </div>

                {abierto && (
                  <div className="mt-3 pt-3" style={{ borderTop: "1px solid #E6E6FF" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Fecha:</span>
                      <div>
                        <CalendarTodayOutlined sx={{ width: "16px", height: "16px", color: "#5656FF", marginRight: "8px" }} />
                        <span style={{ fontSize: "14px", color: "#525252", fontFamily: "Bricolage Grotesque" }}>
                          {s.fecha_creacion ? moment(s.fecha_creacion).local().format("DD/MM/YYYY") : ""}
                        </span>
                      </div>
                    </div>
                    <div className="mb-2" style={{ fontSize: "14px", color: "#525252" }}>
                      {items.map((it, i) => (
                        <div key={i}>{`${it.cantidad} x ${it.tarifa ? it.tarifa.concepto : "Tarifa"}`}</div>
                      ))}
                    </div>
                    {s.estado === "rechazada" && s.observaciones && (
                      <div style={{ color: "#8A1F1F", fontSize: "13px", marginBottom: "8px" }}>
                        <strong>Motivo:</strong> {s.observaciones}
                      </div>
                    )}
                    {s.estado === "aprobada" && s.idcompra && (
                      <button
                        className="btn w-100 mt-1"
                        style={{ backgroundColor: "#E6E6FF", color: "#5656ff", borderRadius: "6px", fontSize: "14px", fontFamily: "Bricolage Grotesque" }}
                        onClick={(e) => { e.stopPropagation(); verTickets(s); }}
                      >
                        <ConfirmationNumberOutlined sx={{ fontSize: "14px", marginRight: "4px" }} />
                        Ver tickets
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // DESKTOP: tabla (misma estética que Mis compras)
  return (
    <TableContainer component={Paper} className="mt-3">
      <Table aria-label="mis solicitudes">
        <TableHead sx={{ backgroundColor: "#E6E6FF" }}>
          <TableRow sx={{ borderColor: "#E6E6FF" }}>
            <TableCell sx={headCellSx}>Evento</TableCell>
            <TableCell sx={headCellSx} align="center">Entradas</TableCell>
            <TableCell sx={headCellSx} align="center">Estado</TableCell>
            <TableCell sx={headCellSx} align="center">Fecha</TableCell>
            <TableCell sx={headCellSx} align="center">Acción</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {solicitudes.map((s) => {
            const estilo = ESTADO_STYLE[s.estado] || ESTADO_STYLE.pendiente;
            const clickable = s.estado === "aprobada" && s.idcompra;
            return (
              <TableRow
                key={s.id}
                hover={!!clickable}
                sx={{ cursor: clickable ? "pointer" : "default" }}
                onDoubleClick={() => clickable && verTickets(s)}
              >
                <TableCell sx={{ fontFamily: "Bricolage Grotesque", color: "#0b0060" }}>
                  {s.evento?.evento || "Evento"}
                  {s.estado === "rechazada" && s.observaciones && (
                    <div style={{ color: "#8A1F1F", fontSize: "12px", marginTop: "2px" }}>
                      Motivo: {s.observaciones}
                    </div>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ fontFamily: "Bricolage Grotesque" }}>{totalEntradas(s)}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={estilo.label} sx={{ backgroundColor: estilo.bg, color: estilo.color, fontFamily: "Bricolage Grotesque" }} />
                </TableCell>
                <TableCell align="center" sx={{ fontFamily: "Bricolage Grotesque" }}>
                  <div className="d-flex justify-content-center align-items-center gap-2">
                    <CalendarTodayOutlined sx={{ width: "18px", height: "18px", color: "#5656FF" }} />
                    {s.fecha_creacion ? moment(s.fecha_creacion).local().format("DD/MM/YYYY") : ""}
                  </div>
                </TableCell>
                <TableCell align="center">
                  {clickable ? (
                    <button
                      className="btn"
                      style={{ backgroundColor: "#E6E6FF", color: "#5656ff", borderRadius: "6px", fontSize: "13px", fontFamily: "Bricolage Grotesque" }}
                      onClick={() => verTickets(s)}
                    >
                      <ConfirmationNumberOutlined sx={{ fontSize: "14px", marginRight: "4px" }} />
                      Ver tickets
                    </button>
                  ) : (
                    <span style={{ color: "#B5BABF", fontSize: "13px" }}>—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MisSolicitudes;
